import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  UserMinus, Loader2, X, Info, ChevronRight, AlertCircle 
} from 'lucide-react';
import { offboardingService } from '../../services/offboardingService';
import { employeeService } from '../../services/employeeService';
import StatusBadge from '../../components/StatusBadge';

const REASON_OPTIONS = ['Resignation', 'Termination', 'End of Contract', 'Other'];

const Offboarding = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [employees, setEmployees] = useState([]); 
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedEmp, setSelectedEmp] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [lastWorkingDate, setLastWorkingDate] = useState(new Date().toISOString().split('T')[0]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const selectedEmployee = employees.find(emp => emp.employee_id === selectedEmp);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const recRes = await offboardingService.getAll();
      const empRes = await employeeService.getEmployees(undefined, 'active');
      setRecords(recRes.data || []);
      setEmployees(empRes.data || []);
    } catch (err) {
      console.error(err);
      setError('Failed to load offboarding data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleStartProcess = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await offboardingService.initiate({
        employee_id: selectedEmp,
        reason: reason,
        notes: notes,
        last_working_date: lastWorkingDate
      });
      setShowModal(false);
      setSelectedEmp('');
      setReason('');
      setNotes('');
      setLastWorkingDate(new Date().toISOString().split('T')[0]);
      loadData();
    } catch (err) {
      const msg = err.data?.[0]?.msg || err.message || 'Error starting process';
      alert(typeof msg === 'string' ? msg : 'Validation Error: Check your inputs');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 pb-10">
      <div className="flex justify-between items-center gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Offboarding</h1>
          <p className="text-sm text-gray-500 font-medium tracking-tight">Clearance and exit documentation management</p>
        </div>
        <button 
          onClick={() => setShowModal(true)}
          className="bg-primary text-white px-5 py-2.5 rounded-xl text-sm font-bold shadow-lg shadow-primary/20 hover:bg-primary-dark transition-all"
        >
          Initiate Exit
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary opacity-20 w-10 h-10" /></div>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {records.length === 0 ? (
            <div className="bg-white p-12 text-center rounded-card border border-dashed border-gray-200">
              <p className="text-gray-400 font-medium italic">No offboarding records found.</p>
            </div>
          ) : (
            records.map((rec) => (
              <div 
                key={rec.id} 
                onClick={() => navigate('/offboarding/' + rec.offboarding_id)}
                className="bg-white p-5 rounded-card border border-gray-100 shadow-soft flex items-center justify-between gap-4 hover:border-primary/20 cursor-pointer transition-all group"
              >
                <div className="space-y-1">
                  <h3 className="font-bold text-gray-900 group-hover:text-primary transition-colors">{rec.employee_name}</h3>
                  <p className="text-xs text-gray-500 font-medium flex items-center gap-3 flex-wrap">
                    <span>{rec.designation}</span>
                    <span className="text-gray-300">|</span>
                    <span>Join Date: <span className="font-semibold text-gray-700">{rec.joining_date}</span></span>
                    <span className="text-gray-300">|</span>
                    <span>Offboard Date: <span className="font-semibold text-gray-700">{rec.last_working_date}</span></span>
                  </p>
                  <div className="pt-1.5"><StatusBadge status={String(rec.status)} /></div>
                </div>
                <div className="flex items-center gap-2">
                   <span className="text-[10px] font-bold text-gray-300 uppercase hidden sm:block">View Details</span>
                   <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-primary transition-all" />
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Initiation Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50 animate-in fade-in">
          <div className="bg-white rounded-card shadow-elevated w-full max-w-md overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-50 flex items-center justify-between font-bold text-gray-900">
              New Exit Process
              <button onClick={() => setShowModal(false)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <form onSubmit={handleStartProcess} className="p-6 space-y-4">
              <div>
                <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1.5 tracking-widest">Employee</label>
                <select 
                  required
                  value={selectedEmp} 
                  onChange={(e) => setSelectedEmp(e.target.value)}
                  className="w-full p-2.5 border border-gray-200 rounded-xl text-sm bg-gray-50 outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="">Select person...</option>
                  {employees.map(emp => (
                    <option key={emp.employee_id} value={emp.employee_id}>{emp.full_name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1.5 tracking-widest">Type of Exit</label>
                <select 
                  required
                  value={reason} 
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full p-2.5 border border-gray-200 rounded-xl text-sm bg-gray-50 outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="">Choose category...</option>
                  {REASON_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1.5 tracking-widest">Last Working Date</label>
                <input
                  type="date"
                  required
                  value={lastWorkingDate}
                  min={selectedEmployee?.joining_date}
                  onChange={(e) => setLastWorkingDate(e.target.value)}
                  className="w-full p-2.5 border border-gray-200 rounded-xl text-sm bg-gray-50 outline-none focus:ring-1 focus:ring-primary"
                />
              </div>

              <div>
                <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1.5 tracking-widest">Notes / Remarks</label>
                <textarea 
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full p-3 border border-gray-200 rounded-xl text-sm bg-gray-50 h-24 outline-none focus:ring-1 focus:ring-primary"
                  placeholder="Additional details (e.g. He is not working properly...)"
                />
              </div>

              <button 
                type="submit" 
                disabled={submitting} 
                className="w-full bg-primary text-white py-3 rounded-xl font-bold flex justify-center items-center gap-2 shadow-lg shadow-primary/20 hover:bg-primary-dark transition-all"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Confirm & Start
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Offboarding;